<header tds-header navigationMenuLabel="Menu">
  <div tds-header-app-name [name]="'general.app_name' | translate" routerLink="/home" class="pointer"></div>
  @if (!isEnvProd) {
    <span class="env-badge" [ngClass]="envName">{{ envName }}</span>
  }

  <tds-header-center>
    @if (mpsUserService.isLoggedIn()) {
      <nav class="tds-header-navigation" attr.aria-label="{{ 'header.aria.navigation_menu' | translate }}">
        @for (route of navigationRoutes; track $index) {
          @if (isMenuItemDisplayed(route)) {
            @if (!route.children || route.children.length === 0) {
              <a tds-header-navigation-link [routerLink]="'/' + route.path" routerLinkActive>
                {{ route.data?.title | translate }}
              </a>
            }
            @if (route.children && route.children.length > 0) {
              <div class="nav-dropdown">
                <a tds-header-navigation-link [isActive]="isChildRouteActive(route)" class="nav-dropdown-trigger">
                  {{ route.data?.title | translate }}
                  <tds-icon name="keyboard_arrow_down" size="sm"></tds-icon>
                </a>
                <div class="nav-dropdown-menu">
                  <div>
                    @for (child of route.children; track child.path) {
                      @if (isMenuItemDisplayed(child) && child.data?.title) {
                        <a
                          [routerLink]="'/' + route.path + '/' + child.path"
                          routerLinkActive="nav-dropdown-item--active"
                        >
                          {{ child.data?.title | translate }}
                        </a>
                      }
                    }
                  </div>
                </div>
              </div>
            }
          }
        }
      </nav>
    }
  </tds-header-center>

  <tds-header-end>
    @if (user()) {
      <app-notification-bell />

      @if (canImpersonate) {
        <div class="impersonate">
          <tds-combobox (selectionChange)="impersonateUser($event)">
            <input tds-combobox-input [formControl]="search" placeholder="{{ 'messages.label.search' | translate }}" />
            @for (user of usersToImpersonate(); track user.igg) {
              <tds-combobox-single-item [value]="user">
                {{ user.igg | uppercase }} - {{ user.firstname }} {{ user.lastname | uppercase }} |
                {{ user.role.label }}
              </tds-combobox-single-item>
            }
          </tds-combobox>
        </div>
      }

      <div
        class="user-menu-wrapper"
        (click)="toggleUserMenu()"
        (keyup.enter)="toggleUserMenu()"
        tabindex="0"
        role="button"
        [attr.aria-expanded]="isUserMenuOpen()"
        [attr.aria-label]="'header.aria.user_menu' | translate"
      >
        <div class="user-badge">
          <span class="display-name">{{ getUserDisplayName() }}</span>
          <tds-icon name="person" variant="red"></tds-icon>
        </div>
        <div class="user-menu-options" [class.open]="isUserMenuOpen()" role="menu" aria-orientation="vertical">
          <!-- Identity block -->
          <div class="user-menu-identity" role="menuitem" aria-disabled="true">
            <span class="user-menu-fullname">{{ user()?.firstname }} {{ user()?.lastname | uppercase }}</span>
            <span class="user-menu-secondary">{{ getUserSecondaryLine() }}</span>
          </div>

          <div class="user-menu-separator" role="separator"></div>

          <!-- Role row -->
          <div class="user-menu-info-row" role="menuitem" aria-disabled="true">
            <span class="user-menu-label">{{ 'header.account.role' | translate }}</span>
            <tds-tag [accent]="getRoleAccent()">
              {{ getUserRoleTranslationKey() | translate }}
            </tds-tag>
          </div>

          <!-- Scope / org entities row -->
          @if (getDisplayEntities().length > 0) {
            <div class="user-menu-info-row" role="menuitem" aria-disabled="true">
              <span class="user-menu-label">{{ 'header.account.scope' | translate }}</span>
              <div class="user-menu-entities">
                @for (entity of getDisplayEntities(); track entity.code) {
                  <tds-tag accent="grey" [title]="entity.code">{{ entity.name_en }}</tds-tag>
                }
              </div>
            </div>
          }

          <!-- Delegation row — only for DELEGATED_MANAGER with at least one date -->
          @if (hasDelegationInfo()) {
            <div class="user-menu-info-row" role="menuitem" aria-disabled="true">
              <span class="user-menu-label">{{ 'header.account.delegation' | translate }}</span>
              <span class="user-menu-delegation-dates">
                @if (user()?.delegation_start) {
                  {{ user()?.delegation_start | appDate }}
                }
                @if (user()?.delegation_start && user()?.delegation_end) {
                  <span aria-hidden="true"> &rarr; </span>
                }
                @if (user()?.delegation_end) {
                  {{ user()?.delegation_end | appDate }}
                }
              </span>
            </div>
          }

          <div class="user-menu-separator" role="separator"></div>

          <!-- Logout link -->
          <a
            [routerLink]="logoutRoute"
            role="menuitem"
            tabindex="0"
            [attr.aria-label]="'header.aria.menu_logout' | translate"
          >
            {{ 'general.main_menu.logout' | translate }}
          </a>
        </div>
      </div>

      @if (translateService.getLangs().length > 1) {
        <div
          class="lang-dropdown"
          (click)="toggleDropdown()"
          (keyup.enter)="toggleDropdown()"
          tabindex="0"
          role="button"
        >
          <a class="lang-link" role="button" attr.aria-label="{{ 'header.aria.change_language' | translate }}">
            <tds-icon name="language" size="sm"></tds-icon>
            <span class="current-lang">{{ getCurrentLangLabel() }}</span>
            <tds-icon name="keyboard_arrow_down" size="sm"></tds-icon>
          </a>
          <div class="lang-options" [class.open]="isDropdownOpen()">
            @for (lang of translateService.getLangs(); track lang) {
              <a
                (click)="switchLanguage(lang)"
                (keyup.enter)="switchLanguage(lang)"
                role="button"
                tabindex="0"
                attr.aria-label="{{ 'header.aria.translate_to' | translate: { lang: getLangLabel(lang) } }}"
              >
                {{ getLangLabel(lang) }}
              </a>
            }
          </div>
        </div>
      }
    }
  </tds-header-end>

  <tds-header-mobile-menu>
    <tds-header-mobile-menu-start>
      @if (mpsUserService.isLoggedIn()) {
        <nav class="tds-header-navigation" attr.aria-label="{{ 'header.aria.navigation_menu' | translate }}">
          @for (route of navigationRoutes; track $index) {
            @if (isMenuItemDisplayed(route)) {
              @if (!route.children || route.children.length === 0) {
                <a tds-header-navigation-link [routerLink]="'/' + route.path" routerLinkActive>
                  {{ route.data?.title | translate }}
                </a>
              }
              @if (route.children && route.children.length > 0) {
                <div
                  class="mobile-nav-group-trigger"
                  [class.mobile-nav-group-trigger--active]="isChildRouteActive(route)"
                  (click)="toggleMobileSubMenu()"
                  (keyup.enter)="toggleMobileSubMenu()"
                  tabindex="0"
                  role="button"
                >
                  {{ route.data?.title | translate }}
                  <tds-icon
                    [name]="isMobileSubMenuOpen() ? 'keyboard_arrow_up' : 'keyboard_arrow_down'"
                    size="sm"
                  ></tds-icon>
                </div>
                @if (isMobileSubMenuOpen()) {
                  @for (child of route.children; track child.path) {
                    @if (isMenuItemDisplayed(child) && child.data?.title) {
                      <a
                        tds-header-navigation-link
                        class="nav-child-link"
                        [routerLink]="'/' + route.path + '/' + child.path"
                        routerLinkActive
                      >
                        {{ child.data?.title | translate }}
                      </a>
                    }
                  }
                }
              }
            }
          }
        </nav>
      }
    </tds-header-mobile-menu-start>
    <tds-header-mobile-menu-end>
      <!-- Mobile identity block -->
      @if (user()) {
        <div class="mobile-user-identity">
          <span class="mobile-user-fullname">{{ user()?.firstname }} {{ user()?.lastname | uppercase }}</span>
          <span class="mobile-user-secondary">{{ getUserSecondaryLine() }}</span>
          <div class="mobile-user-tags">
            <tds-tag [accent]="getRoleAccent()">
              {{ getUserRoleTranslationKey() | translate }}
            </tds-tag>
            @for (entity of getDisplayEntities(); track entity.code) {
              <tds-tag accent="grey" [title]="entity.code">{{ entity.name_en }}</tds-tag>
            }
          </div>
          @if (hasDelegationInfo()) {
            <span class="mobile-user-delegation">
              <span class="mobile-user-delegation-label">{{ 'header.account.delegation' | translate }}:</span>
              @if (user()?.delegation_start) {
                {{ user()?.delegation_start | appDate }}
              }
              @if (user()?.delegation_start && user()?.delegation_end) {
                <span aria-hidden="true"> &rarr; </span>
              }
              @if (user()?.delegation_end) {
                {{ user()?.delegation_end | appDate }}
              }
            </span>
          }
        </div>
      }
      <button tds-header-mobile-menu-action [shouldCloseMenuOnClick]="true" [routerLink]="logoutRoute">
        {{ 'general.main_menu.logout' | translate }}
      </button>
    </tds-header-mobile-menu-end>
  </tds-header-mobile-menu>
</header>
